import * as Types from '../../../__generated__/schema';

import { ActorTeaserAPIToken, ActorTeaserUser } from '../fragments/ActorTeaser.generated';
import { GraphQLError } from 'graphql';
import { gql } from '@apollo/client';
import { ActorTeaser } from '../fragments/ActorTeaser.generated';
import * as Apollo from '@apollo/client';
const defaultOptions = {} as const;
export type GetGeneralSettingsVariables = Types.Exact<{ [key: string]: never }>;

export type GetGeneralSettings = {
  generalSettings: Pick<
    Types.GeneralSettingsConfig,
    | 'displayName'
    | 'email'
    | 'shouldEmailAlertAssignees'
    | 'analyticsConsent'
    | 'scheduledQueriesRequireLimitClause'
    | 'usesPAT'
    | 'timezone'
    | 'publicAPIRootDomain'
    | 'publicGraphqlAPIEndpoint'
    | 'publicRestAPIEndpoint'
    | 'publicScimAPIEndpoint'
    | 'publicWebhookAPIEndpoint'
    | 'lookupTablesLambdaRoleArn'
    | 'snowflakeAdminLambdaRoleArn'
    | 'snowflakeReadonlyLambdaRoleArn'
    | 'deltaControllerRoleArn'
    | 'deltaAdminRoleArn'
    | 'databricksRoleArn'
    | 'processedDataBucket'
    | 'lookupTableQueryExportS3Path'
    | 'pantherAIEnabled'
    | 'pantherAICustomerPrompt'
  > & {
    pantherAIAutoRunOnAlert?: Types.Maybe<
      Pick<
        Types.PantherAIAutoRunOnAlertConfig,
        | 'enabled'
        | 'severities'
        | 'tags'
        | 'runAsUserId'
        | 'deferNotifications'
        | 'deferNotificationsTimeoutMinutes'
      > & { runAsUser?: Types.Maybe<ActorTeaserAPIToken | ActorTeaserUser> }
    >;
    pantherAIAutoResolve?: Types.Maybe<
      Pick<
        Types.PantherAIAutoResolveConfig,
        'enabled' | 'riskScoreThreshold' | 'severities' | 'tags'
      >
    >;
    pantherAIKendra?: Types.Maybe<
      Pick<
        Types.PantherAIKendraConfig,
        'enabled' | 'status' | 'errorMessage' | 'indexId' | 'dataSourceId'
      >
    >;
    pantherAIWebTool?: Types.Maybe<
      Pick<
        Types.PantherAIWebToolConfig,
        'enabled' | 'approvedDomains' | 'forbiddenDomains' | 'requiresApproval'
      >
    >;
    pantherAIRemoteMcp?: Types.Maybe<
      Pick<Types.PantherAIRemoteMCPConfig, 'allowedRedirectDomains' | 'allowedCustomSchemes'>
    >;
    infrastructureInfo?: Types.Maybe<{
      networking: Pick<Types.NetworkingInfo, 'publicIp'>;
      storage: Pick<Types.StorageInfo, 'processedDataRetentionDays'>;
    }>;
  };
  samlConfig: Pick<
    Types.SamlConfig,
    'enabled' | 'idpInitiated' | 'defaultRoleId' | 'metadataUrl' | 'hasMetadataFile' | 'ssoOnly'
  >;
};

export const GetGeneralSettingsDocument = gql`
  query GetGeneralSettings {
    generalSettings {
      displayName
      email
      shouldEmailAlertAssignees
      analyticsConsent
      scheduledQueriesRequireLimitClause
      usesPAT
      timezone
      publicAPIRootDomain
      publicGraphqlAPIEndpoint
      publicRestAPIEndpoint
      publicScimAPIEndpoint
      publicWebhookAPIEndpoint
      lookupTablesLambdaRoleArn
      snowflakeAdminLambdaRoleArn
      snowflakeReadonlyLambdaRoleArn
      deltaControllerRoleArn
      deltaAdminRoleArn
      databricksRoleArn
      processedDataBucket
      lookupTableQueryExportS3Path
      pantherAIEnabled
      pantherAIAutoRunOnAlert {
        enabled
        severities
        tags
        runAsUserId
        runAsUser {
          ...ActorTeaser
        }
        deferNotifications
        deferNotificationsTimeoutMinutes
      }
      pantherAIAutoResolve {
        enabled
        riskScoreThreshold
        severities
        tags
      }
      pantherAICustomerPrompt
      pantherAIKendra {
        enabled
        status
        errorMessage
        indexId
        dataSourceId
      }
      pantherAIWebTool {
        enabled
        approvedDomains
        forbiddenDomains
        requiresApproval
      }
      pantherAIRemoteMcp {
        allowedRedirectDomains
        allowedCustomSchemes
      }
      infrastructureInfo {
        networking {
          publicIp
        }
        storage {
          processedDataRetentionDays
        }
      }
    }
    samlConfig {
      enabled
      idpInitiated
      defaultRoleId
      metadataUrl
      hasMetadataFile
      ssoOnly
    }
  }
  ${ActorTeaser}
`;

/**
 * __useGetGeneralSettings__
 *
 * To run a query within a React component, call `useGetGeneralSettings` and pass it any options that fit your needs.
 * When your component renders, `useGetGeneralSettings` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetGeneralSettings({
 *   variables: {
 *   },
 * });
 */
export function useGetGeneralSettings(
  baseOptions?: Apollo.QueryHookOptions<GetGeneralSettings, GetGeneralSettingsVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<GetGeneralSettings, GetGeneralSettingsVariables>(
    GetGeneralSettingsDocument,
    options,
  );
}
export function useGetGeneralSettingsLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<GetGeneralSettings, GetGeneralSettingsVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<GetGeneralSettings, GetGeneralSettingsVariables>(
    GetGeneralSettingsDocument,
    options,
  );
}
export function useGetGeneralSettingsSuspenseQuery(
  baseOptions?: Apollo.SuspenseQueryHookOptions<GetGeneralSettings, GetGeneralSettingsVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<GetGeneralSettings, GetGeneralSettingsVariables>(
    GetGeneralSettingsDocument,
    options,
  );
}
export type GetGeneralSettingsHookResult = ReturnType<typeof useGetGeneralSettings>;
export type GetGeneralSettingsLazyQueryHookResult = ReturnType<
  typeof useGetGeneralSettingsLazyQuery
>;
export type GetGeneralSettingsSuspenseQueryHookResult = ReturnType<
  typeof useGetGeneralSettingsSuspenseQuery
>;
export type GetGeneralSettingsQueryResult = Apollo.QueryResult<
  GetGeneralSettings,
  GetGeneralSettingsVariables
>;
export function mockGetGeneralSettings({
  data,
  variables,
  errors,
}: {
  data: GetGeneralSettings;
  variables?: GetGeneralSettingsVariables;
  errors?: GraphQLError[];
}) {
  return {
    request: { query: GetGeneralSettingsDocument, variables },
    result: { data, errors },
  };
}
